// ─────────────────────────────────────────────────────────────────────────────
// useAmbientMovement — cola React para o MovementSystem
//
// ARQUITETURA (client-authoritative / híbrido — RPG 16-bit, casual, mundo
// grande com muitos NPCs):
//   • O frontend executa TODA a movimentação ambiental (NPCs de patrulha,
//     pássaros) localmente, a partir do JSON. Zero ida-e-volta ao servidor.
//   • O servidor PHP só sincroniza o que é crítico: posição do JOGADOR
//     (POST /api/user/hub/move valida colisão) e ações globais do mundo
//     (iniciar batalha, forja, etc. via REST / WebSocket de turno).
//   • Swoole/server-authoritative só seria necessário para PvP em tempo real
//     de ritmo acelerado — não é o caso deste jogo.
//
// O hook cria UM MovementSystem, carrega os JSON uma vez e devolve um REF
// estável — seguro de ler dentro de callbacks de render com deps `[]`.
// ─────────────────────────────────────────────────────────────────────────────

import { type MutableRefObject, useEffect, useRef, useState } from 'react';
import { MovementSystem } from './MovementSystem';
import type { Vec2 } from './MovementComponent';

export interface AmbientNpcSpec {
  /** id único da entidade no mundo (ex.: 'blacksmith', 'guard_1'). */
  entityId: string;
  /** id do perfil em npc_behavior.json. */
  profileId: string;
  /** posição inicial (sobrescreve o 1º waypoint). */
  startPos?: Vec2;
}

export interface UseAmbientMovementOptions {
  /** Liga o sistema. Passe `false` para desativar (ex.: fora do Hub). */
  enabled?: boolean;
  /** ids de spawners de pássaros a ativar (de ambient_birds.json). */
  birdSpawners?: string[];
  /** NPCs a registrar de imediato. */
  npcs?: AmbientNpcSpec[];
  /** URLs alternativas dos JSON (default: /data/movement/*.json). */
  npcUrl?: string;
  birdsUrl?: string;
}

export interface AmbientMovementHandle {
  /** Ref estável para o sistema (null até `ready`). Leia `.current`. */
  ref: MutableRefObject<MovementSystem | null>;
  ready: boolean;
  error: string | null;
}

export function useAmbientMovement(opts: UseAmbientMovementOptions = {}): AmbientMovementHandle {
  const { enabled = true, birdSpawners = [], npcs = [], npcUrl, birdsUrl } = opts;
  const ref = useRef<MovementSystem | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // chaves serializadas — recria só quando a composição muda de fato
  const spawnersKey = birdSpawners.join(',');
  const npcsKey = npcs.map(n => `${n.entityId}:${n.profileId}`).join(',');

  useEffect(() => {
    if (!enabled) {
      ref.current = null;
      setReady(false);
      return;
    }

    let cancelled = false;
    const sys = new MovementSystem();

    sys.init(npcUrl, birdsUrl)
      .then(() => {
        if (cancelled) return;
        for (const id of birdSpawners) {
          try { sys.addBirdSpawner(id); } catch (e) { console.warn(e); }
        }
        for (const n of npcs) {
          try { sys.addNpc(n.entityId, n.profileId, n.startPos); } catch (e) { console.warn(e); }
        }
        ref.current = sys;
        setReady(true);
        setError(null);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        console.warn('[useAmbientMovement] init falhou:', e.message);
        setError(e.message);
        setReady(false);
      });

    return () => {
      cancelled = true;
      ref.current = null;
      setReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, spawnersKey, npcsKey, npcUrl, birdsUrl]);

  return { ref, ready, error };
}
